import { t } from "@lingui/macro";
import type { User } from "@src/__generated__/graphql";
import { ProjectTypeEnum } from "@src/__generated__/urql-graphql";
import { appStore } from "@src/stores/AppStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import { toApiDate } from "@src/utils/dates";
import { denominate } from "@src/utils/formatters";
import {
  arrayLengthMin,
  onlyNumericWithoutDecimal,
  required,
} from "@src/utils/forms/validators";
import { isAfter, isBefore, subDays } from "date-fns";
import { FieldState, FormState } from "formstate";
import { omit } from "lodash";
import { formatDate } from "../../../../ui-kit";
import type { StoreProjectForm } from "./StoreProjectForm";

export class FormProject {
  store: StoreProjectForm;

  private startDateValidator = () => {
    if (
      this.store.lowestPossibleDateForStartDateChange &&
      this.start_date.value &&
      !isAfter(
        this.start_date.value,
        subDays(this.store.lowestPossibleDateForStartDateChange, 1),
      )
    ) {
      return t`Lowest date possible is ${formatDate(
        this.store.lowestPossibleDateForStartDateChange,
      )}`;
    }
    if (
      !this.store.lowestPossibleDateForStartDateChange &&
      this.store.lowestDateWithOpenProgress &&
      this.start_date.value &&
      !isAfter(
        this.start_date.value,
        subDays(this.store.lowestDateWithOpenProgress, 1),
      )
    ) {
      return t`Lowest date possible is ${formatDate(this.store.lowestDateWithOpenProgress)}`;
    }
    if (
      !(this.start_date.value instanceof Date) ||
      !(this.expected_end_date.value instanceof Date)
    )
      return undefined;
    if (isAfter(this.start_date.value, this.expected_end_date.value)) {
      return t`Start date must be before end date`;
    }
    return undefined;
  };

  private endDateValidator = () => {
    if (
      !(this.start_date.value instanceof Date) ||
      !(this.expected_end_date.value instanceof Date)
    )
      return undefined;
    if (isBefore(this.expected_end_date.value, this.start_date.value)) {
      return t`End date must be after start date`;
    }
    return undefined;
  };

  private requiredIfNotTemplate = ($: Date | string | number | undefined) => {
    if (this.store.isTemplate) return null;
    return required($);
  };

  private requiredCategoryIfEnabled = ($: string[]) => {
    if (this.store.isTemplate) return null;
    if (!appStore.workspaceStore.settings?.require_project_category)
      return null;
    return arrayLengthMin(1)($);
  };

  title = new FieldState("").validators(required);
  code = new FieldState("").validators();
  note = new FieldState("").validators();
  brand_id = new FieldState("").validators(
    this.requiredIfNotTemplate,
    onlyNumericWithoutDecimal,
  );
  start_date = new FieldState<Date | undefined>(new Date()).validators(
    required,
    this.startDateValidator,
  );
  expected_end_date = new FieldState<Date | undefined>(undefined).validators(
    this.requiredIfNotTemplate,
    this.endDateValidator,
  );
  project_manager_id = new FieldState("").validators(
    this.requiredIfNotTemplate,
    onlyNumericWithoutDecimal,
  );
  other_project_manager_ids = new FieldState<string[]>([]);
  contact_id = new FieldState("").validators(onlyNumericWithoutDecimal);
  team_id = new FieldState(appStore.authStore?.user?.team?.id ?? "").validators(
    onlyNumericWithoutDecimal,
  );
  project_type = new FieldState<ProjectTypeEnum>(
    ProjectTypeEnum.FixedPrice,
  ).validators(required);
  billable = new FieldState(true);
  budget_id = new FieldState<string | undefined>(undefined);
  category_ids = new FieldState<string[]>([]).validators(
    this.requiredCategoryIfEnabled,
  );
  propagate_billable = new FieldState(true);
  rate_card = new FieldState<string | undefined>(undefined);
  client_ids = new FieldState<User["id"][]>([]);

  form = new FormState({
    title: this.title,
    code: this.code,
    note: this.note,
    brand_id: this.brand_id,
    start_date: this.start_date,
    expected_end_date: this.expected_end_date,
    project_manager_id: this.project_manager_id,
    other_project_manager_ids: this.other_project_manager_ids,
    contact_id: this.contact_id,
    team_id: this.team_id,
    billable: this.billable,
    project_type: this.project_type,
    budget_id: this.budget_id,
    category_ids: this.category_ids,
    propagate_billable: this.propagate_billable,
    rate_card: this.rate_card,
    client_ids: this.client_ids,
  });

  constructor(store: StoreProjectForm) {
    this.store = store;
  }

  validate = async () => {
    const res = await this.form.validate();

    return res.hasError
      ? null
      : omit(
          {
            title: this.form.$.title.$,
            code: this.form.$.code.$,
            note: this.form.$.note.$,
            brand_id:
              this.form.$.brand_id.$ !== ""
                ? this.form.$.brand_id.$
                : undefined,
            start_date: this.form.$.start_date.$
              ? toApiDate(this.form.$.start_date.$)
              : "",
            expected_end_date: this.form.$.expected_end_date.$
              ? toApiDate(this.form.$.expected_end_date.$)
              : null,
            project_manager_id: this.form.$.project_manager_id.$,
            other_project_manager_ids: this.form.$.other_project_manager_ids.$,
            contact_id: this.form.$.contact_id.$,
            team_id: this.form.$.team_id.$,
            billable: this.form.$.billable.$,
            type: this.form.$.project_type.$,
            budget_id: this.form.$.budget_id.$,
            category_ids: this.form.$.category_ids.$,
            rate_card_id: this.form.$.rate_card.$,
            rate_card_exchange_rate: this.form.$.rate_card.$
              ? this.store.showExchangeRateInput
                ? this.store.exchangeRate.$.denominatedValue
                : denominate(1, EXCHANGE_RATE_DENOMINATION)
              : undefined,
            user_ids: this.form.$.client_ids.$,
            is_template: this.store.isTemplate,
          },
          this.store.isTemplate ? ["expected_end_date"] : [],
        );
  };
}
